import { parseISO, format, differenceInMinutes } from "date-fns";
import Hey from "@/lib/hey";

export interface FlightStatus {
  id: string;
  departurePlace: string;
  arrivalPlace: string;
  scheduledDepartureLocalDate: string;
  departureTimezone: string;
  scheduledArrivalLocalDate: string;
  arrivalTimezone: string;
  latestStatus?: {
    estimatedDepartureLocalDate: string;
    departureTimezone: string;
    estimatedArrivalLocalDate: string;
    arrivalTimezone: string;
    flightStatus?: string;
    departureTerminal?: string;
    departureGate?: string;
    arrivalTerminal?: string;
    arrivalGate?: string;
    departureCheckInDesk?: string;
    actualDepartureLocalDate?: string;
    actualArrivalLocalDate?: string;
    arrivalBaggageClaim?: string;
  };
}

export interface FlightStatusResponse {
  data: FlightStatus[];
}

let _flightStatusCacheTimestamp: { [key: string]: number } = {};
let _flightStatusCache: { [key: string]: FlightStatus[] } = {};

export const getFlightStatus = async (flightIds: string[]): Promise<FlightStatus[]> => {
  try {
    console.debug('🌐 Flight IDs Param:', flightIds);
    if (!flightIds.length) return [];
    const flightIdsParam = flightIds.join(',');
   
    const url = `/api/flight-status/latest?flightIds=${flightIdsParam}`;

    // if (_flightStatusCacheTimestamp[url] && _flightStatusCacheTimestamp[url] < Date.now() - 1000 * 30) {
    //   delete _flightStatusCache[url];
    //   delete _flightStatusCacheTimestamp[url];
    // }
    // if (_flightStatusCache[url]) {
    //   console.debug('🌐 Flight status cache hit:', _flightStatusCache[url]);
    //   return _flightStatusCache[url];
    // }
    
    console.debug('🌐 Flight status API URL:', url);
    
    await Hey.HeyUtils.waitInit();
    const response = await Hey.HeyUtils.getInstance().authService.callHeyApiOAGTech('GET', url, null);
    
    console.debug('🌐 API Response:', response);
    
    if (!response.success) {
      console.error('Error fetching flight status:', response.error);
      console.error('Response details:', JSON.stringify(response));
      
      // For 404 errors, just return an empty array instead of throwing
      // This is an expected case when flight status is not available
      if (response.status === 404) {
        return [];
      }
      
      throw new Error(`API Error: ${response.error}`);
    }
    
    console.debug('✅ Flight status data received:', response.data || []);
    _flightStatusCache[url] = response.data || [];
    _flightStatusCacheTimestamp[url] = Date.now();
    return response.data || [];
  } catch (error) {
    console.error('Error fetching flight status:', error);
    if (error instanceof Error) {
      console.error('Error details:', error.message, error.stack);
    }
    throw error;
  }
};

export const getFlightStatusLabel = (scheduled: string, estimated: string): { 
  label: string; 
  color: string;
  diffMinutes: number;
} => {
  if (!estimated) return { label: 'No Status', color: 'text-gray-500', diffMinutes: 0 };
  
  try {
    const scheduledDate = parseISO(scheduled || "1970");
    const estimatedDate = parseISO(estimated || "1970");
    
    // Check if dates are valid before calculating difference
    if (isNaN(scheduledDate.getTime()) || isNaN(estimatedDate.getTime())) {
      // console.error('Invalid date detected:', { scheduled, estimated });
      return { label: 'No Status', color: 'text-gray-500', diffMinutes: 0 };
    }
    
    // Log the dates for debugging
    console.debug('Scheduled date:', scheduledDate, format(scheduledDate, 'yyyy-MM-dd HH:mm:ss'));
    console.debug('Estimated date:', estimatedDate, format(estimatedDate, 'yyyy-MM-dd HH:mm:ss'));
    
    // Check if dates are on different days
    const scheduledDay = format(scheduledDate, 'yyyy-MM-dd');
    const estimatedDay = format(estimatedDate, 'yyyy-MM-dd');
    
    if (scheduledDay !== estimatedDay) {
      // If dates are on different days, calculate time difference considering only the time portion
      // This prevents the 1440 mins (24 hours) issue when comparing dates from different days
      const scheduledHours = scheduledDate.getHours();
      const scheduledMinutes = scheduledDate.getMinutes();
      const estimatedHours = estimatedDate.getHours();
      const estimatedMinutes = estimatedDate.getMinutes();
      
      const scheduledTotalMinutes = scheduledHours * 60 + scheduledMinutes;
      const estimatedTotalMinutes = estimatedHours * 60 + estimatedMinutes;
      
      const diffMinutes = estimatedTotalMinutes - scheduledTotalMinutes;
      console.debug('Time difference (different days):', diffMinutes);
      
      if (diffMinutes > 1) {
        return { 
          label: `Delayed - ${Math.abs(diffMinutes)} mins`, 
          color: 'text-red-500',
          diffMinutes 
        };
      } else if (diffMinutes < -1) {
        return { 
          label: `Early - ${Math.abs(diffMinutes)} mins`, 
          color: 'text-green-500',
          diffMinutes 
        };
      } else {
        return { 
          label: 'On Time', 
          color: 'text-green-500',
          diffMinutes 
        };
      }
    } else {
      // If dates are on the same day, use differenceInMinutes
      const diffMinutes = differenceInMinutes(estimatedDate, scheduledDate);
      console.debug('Time difference (same day):', diffMinutes);
      
      if (diffMinutes > 1) {
        return { 
          label: `Delayed - ${Math.abs(diffMinutes)} mins`, 
          color: 'text-red-500',
          diffMinutes 
        };
      } else if (diffMinutes < -1) {
        return { 
          label: `Early - ${Math.abs(diffMinutes)} mins`, 
          color: 'text-green-500',
          diffMinutes 
        };
      } else {
        return { 
          label: 'On Time', 
          color: 'text-green-500',
          diffMinutes 
        };
      }
    }
  } catch (error) {
    console.error('Error calculating flight status:', error);
    return { label: 'No Status', color: 'text-gray-500', diffMinutes: 0 };
  }
};

export const formatFlightTime = (timeString: string): string => {
  if (!timeString) return 'N/A';
  try {
    const date = parseISO(timeString || "1970");
    return format(date, 'h:mm a, MMM d');
  } catch (error) {
    console.error('Error formatting time:', error);
    return timeString;
  }
};
